import React, { useEffect, useState } from 'react';
import '../styles/pages/settings.css';
import axios from '../api/api1';
import { getDashboardData } from '../services/api';
import { SettingsIcon } from '../header/Sidebar.jsx';

export function SettingsPage({ toast }) {
  const [tab, setTab] = useState('QR');
  const [saving, setSaving] = useState(false);
  const [qrSettingsLoaded, setQrSettingsLoaded] = useState(false);

  const [pwd, setPwd] = useState({ current: '', new: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const [qrPreview, setQrPreview] = useState(() => {
    try {
      const tenant = JSON.parse(localStorage.getItem('tenantinfo') || '{}');
      const qr = localStorage.getItem('payment_qr') || tenant.upi_qr_code || '';
      return /^(https?:|data:|blob:)/i.test(qr) ? qr : `${axios.defaults.baseURL || ''}${qr}`;
    } catch {
      return localStorage.getItem('payment_qr') || '';
    }
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [qrDeleting, setQrDeleting] = useState(false);
  const [isTenantOwner] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem('userinfo') || '{}');
      const role = String(user.role || '').toLowerCase().replace(/[\s_-]/g, '');
      return role === 'tenantowner';
    } catch {
      return false;
    }
  });

  const getTenantId = () => {
    try {
      const t = JSON.parse(localStorage.getItem('tenantinfo') || '{}');
      return t.id || t.tenantId || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const dashRes = await getDashboardData(null, true).catch(() => ({ dashboard: {} }));
        const dash = dashRes?.dashboard || {};

        const tenantStr = localStorage.getItem('tenantinfo');
        const tenant = tenantStr ? JSON.parse(tenantStr) : {};
        const qr = dash.payment_qr || dash.qr_url || tenant.upi_qr_code || localStorage.getItem('payment_qr');
        if (qr) {
          const finalUrl = qr.startsWith('http') || qr.startsWith('data:') || qr.startsWith('blob:') ? qr : `${axios.defaults.baseURL || ''}${qr}`;
          setQrPreview(finalUrl);
        }
      } catch (error) {
        console.error('Failed to load payment QR settings:', error);
      } finally {
        setQrSettingsLoaded(true);
      }
    };
    load();
  }, []);

  const handleQr = async (file) => {
    if (!file) return;
    if (!isTenantOwner) {
      toast('Access denied: only Tenant Owners can manage the QR code.');
      return;
    }
    if (!qrSettingsLoaded) {
      toast('Please wait while the current payment QR is checked.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast('File size must be less than 5MB');
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast('Please select an image file');
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = e => setUploadPreview(typeof e.target?.result === 'string' ? e.target.result : null);
    reader.readAsDataURL(file);
  };

  const confirmUpload = async () => {
    if (!selectedFile) return;
    const tenantId = getTenantId();
    if (!tenantId) {
      toast('Tenant not found');
      return;
    }
    if (qrPreview && !window.confirm('Replace the current QR code? The existing QR will be removed before the new image uploads. If the upload fails, you will need to upload a QR code again.')) {
      return;
    }
    setSaving(true);
    let existingQrDeleted = false;
    try {
      if (qrPreview) {
        const deleteResponse = await axios.delete('/api/upload/upi-qr/delete', {
          headers: { 'X-Tenant-ID': tenantId.toString() },
        });
        if (!deleteResponse.data?.success) {
          throw new Error(deleteResponse.data?.error || 'Failed to remove the existing QR code');
        }
        existingQrDeleted = true;
        setQrPreview('');
        localStorage.removeItem('payment_qr');
        const cachedTenant = JSON.parse(localStorage.getItem('tenantinfo') || '{}');
        cachedTenant.upi_qr_code = null;
        localStorage.setItem('tenantinfo', JSON.stringify(cachedTenant));
      }

      const fd = new FormData();
      fd.append('file', selectedFile);
      const response = await axios.post('/api/upload/upi-qr', fd, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Tenant-ID': tenantId.toString(),
        },
      });
      if (!response.data?.success || !response.data?.image_url) {
        throw new Error(response.data?.error || 'Failed to upload QR code');
      }
      const url = response.data.image_url;
      const absoluteUrl = /^https?:|^(data|blob):/i.test(url)
        ? url
        : `${axios.defaults.baseURL || ''}${url}`;
      setQrPreview(absoluteUrl);
      localStorage.setItem('payment_qr', absoluteUrl);
      const tenant = JSON.parse(localStorage.getItem('tenantinfo') || '{}');
      tenant.upi_qr_code = url;
      localStorage.setItem('tenantinfo', JSON.stringify(tenant));
      setSelectedFile(null);
      setUploadPreview(null);
      setShowUploadModal(false);
      toast(existingQrDeleted ? 'UPI QR code replaced successfully!' : 'UPI QR code uploaded successfully!');
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to upload QR code';
      toast(existingQrDeleted ? `${message}. The previous QR was removed; upload a QR code again.` : message);
    } finally { setSaving(false); }
  };

  const handleQrDelete = async () => {
    if (!isTenantOwner) {
      toast('Access denied: only Tenant Owners can manage the QR code.');
      return;
    }
    if (!qrPreview) {
      toast('No QR code to delete');
      return;
    }
    if (!window.confirm('Are you sure you want to delete the UPI QR code? You will need to upload a new one to accept UPI payments.')) return;
    const tenantId = getTenantId();
    if (!tenantId) {
      toast('Tenant not found');
      return;
    }
    setQrDeleting(true);
    try {
      const response = await axios.delete('/api/upload/upi-qr/delete', {
        headers: { 'X-Tenant-ID': tenantId.toString() },
      });
      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Failed to delete QR code');
      }
      setQrPreview('');
      localStorage.removeItem('payment_qr');
      const tenant = JSON.parse(localStorage.getItem('tenantinfo') || '{}');
      tenant.upi_qr_code = null;
      localStorage.setItem('tenantinfo', JSON.stringify(tenant));
      setShowQRModal(false);
      toast('UPI QR code deleted successfully');
    } catch (err) {
      toast(err?.response?.data?.error || err?.message || 'Failed to delete QR code');
    } finally { setQrDeleting(false); }
  };

  const clearQrUpload = () => {
    setSelectedFile(null);
    setUploadPreview(null);
    setShowUploadModal(false);
  };

  useEffect(() => {
    if (!showUploadModal && !showQRModal) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = event => {
      if (event.key !== 'Escape') return;
      if (showUploadModal && !saving) {
        setSelectedFile(null);
        setUploadPreview(null);
        setShowUploadModal(false);
      }
      if (showQRModal && !qrDeleting) setShowQRModal(false);
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showUploadModal, showQRModal, saving, qrDeleting]);

  const handlePwd = async (event) => {
    event.preventDefault();
    if (!pwd.current) {
      setPasswordError('Current password is required');
      return;
    }
    if (!pwd.new) {
      setPasswordError('New password is required');
      return;
    }
    if (pwd.new.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    if (pwd.new !== pwd.confirm) {
      setPasswordError('New passwords do not match');
      return;
    }

    setSaving(true);
    setPasswordError('');
    try {
      await axios.put('/api/users/me', {
        password: pwd.new,
        old_password: pwd.current,
      });
      toast('Password changed successfully');
      setPwd({ current: '', new: '', confirm: '' });
    } catch (err) {
      setPasswordError(err?.response?.data?.error || err?.response?.data?.message || 'Failed to change password');
    } finally { setSaving(false); }
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <span className="settings-title-icon" aria-hidden="true">
          <SettingsIcon size={20} />
        </span>
        <div>
          <h1>Settings</h1>
          <p>Manage your StudyRoom configuration</p>
        </div>
      </div>
      <div className="panel settings-panel">
        <div className="settings-tabs">
          <button className={tab==='QR'?'active':''} onClick={()=>setTab('QR')}>
            <svg className="tab-ic" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2v2h-2zM18 14h2v6h-6v-2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M7 7h.01M17 7h.01M7 17h.01" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
            <span>QR Management</span>
          </button>
          <button className={tab==='Account'?'active':''} onClick={()=>setTab('Account')}>
            <svg className="tab-ic" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.7" />
              <path d="M5 20a7 7 0 0 1 14 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
            <span>Account</span>
          </button>
        </div>

        {tab==='QR' && (
          <div className="settings-body">
            <h3>QR Code Management</h3>
            <p className="s-desc">Upload your payment QR code so students can scan and pay.</p>
            {!isTenantOwner ? (
              <div className="qr-access-notice" role="status">
                Only Tenant Owners can view or manage the payment QR code.
              </div>
            ) : qrPreview ? (
              <div className="qr-current-card">
                <img src={qrPreview} className="qr-preview-img" alt="Current UPI payment QR code" />
                <span className="qr-status"><span aria-hidden="true" /> Active</span>
                <p>Students use this QR code to make UPI payments.</p>
                <div className="qr-current-actions">
                  <button className="button primary" onClick={() => setShowQRModal(true)}>View details</button>
                  <button className="button" onClick={() => setShowUploadModal(true)} disabled={qrDeleting || !qrSettingsLoaded}>
                    Edit / Replace QR
                  </button>
                  <button className="button qr-delete-button" onClick={handleQrDelete} disabled={qrDeleting || !qrSettingsLoaded}>
                    {qrDeleting ? 'Deleting…' : 'Delete QR code'}
                  </button>
                </div>
              </div>
            ) : !qrSettingsLoaded ? (
              <div className="qr-access-notice" role="status">Checking current payment QR…</div>
            ) : (
              <div className="qr-drop">
                <div className="qr-icon-box" aria-hidden="true">▦</div>
                <b>Upload your UPI QR code</b>
                <small>Image files up to 5MB</small>
                <button className="qr-upload-btn" onClick={() => setShowUploadModal(true)}>
                  Choose QR image
                </button>
              </div>
            )}

            {showUploadModal && isTenantOwner && (
              <div
                className="qr-modal-overlay"
                onClick={event => {
                  if (event.target === event.currentTarget && !saving) clearQrUpload();
                }}
              >
                <section className="qr-modal" role="dialog" aria-modal="true" aria-labelledby="qr-upload-title">
                  <header className="qr-modal-header">
                    <div>
                      <span className="qr-modal-eyebrow">PAYMENT SETTINGS</span>
                      <h3 id="qr-upload-title">{qrPreview ? 'Replace UPI QR code' : 'Upload UPI QR code'}</h3>
                      <p>{qrPreview ? 'Choose a new QR image to replace the current payment QR.' : 'Add the QR image students will scan to pay.'}</p>
                    </div>
                    <button
                      type="button"
                      className="qr-modal-close"
                      aria-label="Close upload dialog"
                      disabled={saving}
                      onClick={clearQrUpload}
                    >×</button>
                  </header>
                  {qrPreview && (
                    <p className="qr-replace-notice">
                      The current QR will be removed before the replacement uploads. UPI payments may be unavailable if the upload fails.
                    </p>
                  )}
                  <div
                    className={`qr-modal-content${uploadPreview ? ' has-preview' : ''}`}
                    onDragOver={event => event.preventDefault()}
                    onDrop={event => {
                      event.preventDefault();
                      handleQr(event.dataTransfer.files[0]);
                    }}
                  >
                    {uploadPreview ? (
                      <>
                        <img src={uploadPreview} className="qr-modal-preview" alt="Selected QR code preview" />
                        <div className="qr-file-info">
                          <strong>{selectedFile?.name}</strong>
                          <span>{selectedFile ? `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB` : ''}</span>
                        </div>
                        <label className="qr-change-file">
                          Choose a different image
                          <input
                            type="file"
                            accept="image/*"
                            onChange={event => {
                              handleQr(event.target.files[0]);
                              event.target.value = '';
                            }}
                          />
                        </label>
                      </>
                    ) : (
                      <>
                        <div className="qr-modal-upload-icon" aria-hidden="true">↑</div>
                        <strong>Drop your QR image here</strong>
                        <span>or choose an image from your device</span>
                        <label className="qr-modal-select">
                          Browse files
                          <input
                            type="file"
                            accept="image/*"
                            onChange={event => {
                              handleQr(event.target.files[0]);
                              event.target.value = '';
                            }}
                          />
                        </label>
                        <small>Image files · Maximum size 5MB</small>
                      </>
                    )}
                  </div>
                  <footer className="qr-modal-actions">
                    <button type="button" className="qr-modal-cancel" onClick={clearQrUpload} disabled={saving}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="qr-modal-submit"
                      onClick={confirmUpload}
                      disabled={!selectedFile || saving}
                    >
                      {saving ? 'Uploading…' : qrPreview ? 'Replace QR code' : 'Upload QR code'}
                    </button>
                  </footer>
                </section>
              </div>
            )}

            {showQRModal && qrPreview && isTenantOwner && (
              <div className="qr-modal-overlay" onClick={() => setShowQRModal(false)}>
                <section
                  className="qr-modal qr-details-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="qr-details-title"
                  onClick={event => event.stopPropagation()}
                >
                  <header className="qr-modal-header">
                    <div>
                      <span className="qr-modal-eyebrow">PAYMENT SETTINGS</span>
                      <h3 id="qr-details-title">UPI QR code details</h3>
                    </div>
                    <button type="button" className="qr-modal-close" aria-label="Close details" onClick={() => setShowQRModal(false)}>×</button>
                  </header>
                  <div className="qr-details-content">
                    <img src={qrPreview} alt="UPI payment QR code" />
                    <dl>
                      <div><dt>Status</dt><dd className="qr-detail-active">Active</dd></div>
                      <div><dt>Usage</dt><dd>Student payments</dd></div>
                      <div><dt>Last updated</dt><dd>{new Date().toLocaleDateString()}</dd></div>
                    </dl>
                    <p className="qr-delete-warning">
                      Deleting this QR code will disable UPI payments until a new one is uploaded.
                    </p>
                    <div className="qr-modal-actions">
                      <a href={qrPreview} download="upi-qr-code.png" className="qr-modal-submit">Download QR</a>
                      <button type="button" className="qr-modal-delete" onClick={handleQrDelete} disabled={qrDeleting}>
                        {qrDeleting ? 'Deleting…' : 'Delete QR code'}
                      </button>
                    </div>
                  </div>
                </section>
              </div>
            )}
          </div>
        )}

        {tab==='Account' && (
          <div className="settings-body">
            <h3>Account Settings</h3>
            <p className="s-desc">Change the password you use to sign in to your account.</p>
            <form className="settings-password-form" onSubmit={handlePwd}>
              <label className="s-label" htmlFor="settings-current-password">
                Current password
                <input
                  id="settings-current-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={pwd.current}
                  onChange={e => { setPwd({ ...pwd, current: e.target.value }); setPasswordError(''); }}
                  placeholder="Enter current password"
                />
              </label>
              <label className="s-label" htmlFor="settings-new-password">
                New password
                <input
                  id="settings-new-password"
                  type="password"
                  autoComplete="new-password"
                  minLength={6}
                  required
                  value={pwd.new}
                  onChange={e => { setPwd({ ...pwd, new: e.target.value }); setPasswordError(''); }}
                  placeholder="At least 6 characters"
                />
              </label>
              <label className="s-label" htmlFor="settings-confirm-password">
                Confirm new password
                <input
                  id="settings-confirm-password"
                  type="password"
                  autoComplete="new-password"
                  minLength={6}
                  required
                  value={pwd.confirm}
                  onChange={e => { setPwd({ ...pwd, confirm: e.target.value }); setPasswordError(''); }}
                  placeholder="Re-enter new password"
                />
              </label>
              {passwordError && <p className="settings-form-error" role="alert">{passwordError}</p>}
              <button type="submit" className="button primary s-save" disabled={saving}>
                {saving ? 'Updating…' : 'Update password'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}